import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import type { FC } from 'react';
import React, { useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import type { ContentHeaderUnifiedQuery as ContentHeaderUnifiedQueryType } from '../__types__/ContentHeaderUnifiedQuery.ts';
import { ContentHeaderUnifiedQuery } from '../ContentHeaderUnifiedQuery.graphql.ts';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT } from '../defaultQueryVariables.js';

import { LivePagesByLineWithContributorsComponent } from './LivePagesByLineWithContributorsComponent.tsx';
import { LivePagesByLinePlaceholderComponent } from './LivePagesByLinePlaceholderComponent.tsx';
import type { QueryContentType, QueryType, QueryVariablesType } from './byLineTypes.ts';
import { toUserProps } from './byLineUtils.tsx';

type LivePagesByLineProps = {
	contentId: string;
	contentType: string;
	viewMode?: boolean;
	isPublishingContent: boolean;
	isKnowledgeBaseSpace: boolean;
	userOffline: boolean;
	editorApi?: FullPageEditorPresetAPI | null;
	isUnpublishedDraft?: boolean;
	hasByLineContributors: boolean;
	hasByLineExtensions: boolean;
	hasReactions: boolean;
};

export const LivePagesByLine: FC<LivePagesByLineProps> = ({
	contentId,
	contentType,
	viewMode,
	isPublishingContent,
	isKnowledgeBaseSpace,
	userOffline,
	editorApi,
	isUnpublishedDraft,
	hasByLineContributors,
	hasByLineExtensions,
	hasReactions,
}) => {
	const isLivePage = useIsCurrentPageLive();

	const [spaceKey] = usePageSpaceKey();
	const lastLoadedQueryData = useRef<QueryType | null>(null);
	const renderServerPlaceholder = useRenderServerPlaceholder();

	// eslint-disable-next-line prefer-const
	let { data, loading, error } = useQuery<QueryType, QueryVariablesType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		ContentHeaderUnifiedQuery,
		{
			variables: {
				contentId,
				spaceKey,
				// Don't omit versionOverride, embeddedContentRender and limit
				// It's an Apollo bug that if preloader these needs to be match preloaded query
				// Also doesn't work if removing them from preloader variables HOT-109035
				versionOverride: null,
				embeddedContentRender: null,
				limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
				includeGeneratedBy: fg('cc-activation-user-generated-by-byline'),
				...(isUnpublishedDraft && !isLivePage && { status: ['draft'] }),
			},
			context: {
				single: true,
			},
			fetchPolicy: 'cache-and-network',
		},
	);

	const renderByLine = () => {
		// HACK: status: ['draft'] for unpublished drafts results in a weird case where loading is true and we also have the data. Need to investigate this further, but this hack should work for now.
		// happens because isUnpublishedDraft goes from false to true during SSR. ideally we would either always get the right data, or not need it (pass status: ['draft', 'current'] all the time - which could cause caching issues)
		const hasSSRData = renderServerPlaceholder && Boolean(data);
		if (loading && !hasSSRData) {
			// If we're loading because the LivePagesByLineWithContributorsComponent called our
			// fetchAllContributors, then we should continue the render with data
			// cached from the previous query (the one with the low
			// contributorsFetchLimit). It's important to not bring the skeletons back
			// up, as that'd kill the opening of the dropdown.
			if (lastLoadedQueryData.current) {
				data = lastLoadedQueryData.current;
			} else {
				return <LivePagesByLinePlaceholderComponent />;
			}
		} else {
			if (!error) lastLoadedQueryData.current = data || null;
		}

		const content: QueryContentType | null | undefined = (data as ContentHeaderUnifiedQueryType)
			?.content?.nodes?.[0];

		if (error) {
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
				return null;
			} else {
				// TODO: unknown errors should have a nicer UI within `<ErrorDisplay>`, but we don't have such UI for now
				// so returning empty `<ErrorDisplay>` here.
				return <ErrorDisplay error={error} />;
			}
		} else if (!content) {
			return null;
		}

		const contributorProps = makeByLineContributorsComponentProps(content, data);
		return (
			<LivePagesByLineWithContributorsComponent
				{...contributorProps}
				contentId={contentId}
				contentType={contentType}
				viewMode={viewMode}
				isPublishingContent={isPublishingContent}
				isKnowledgeBaseSpace={isKnowledgeBaseSpace}
				userOffline={userOffline}
				editorApi={editorApi}
				isUnpublishedDraft={isUnpublishedDraft}
				hasByLineContributors={hasByLineContributors}
				hasByLineExtensions={hasByLineExtensions}
				hasReactions={hasReactions}
			/>
		);
	};

	return <ErrorBoundary attribution={Attribution.PAGE_MODES}>{renderByLine()}</ErrorBoundary>;
};

// Exported for testing only
export const makeByLineContributorsComponentProps = (content, data) => {
	const author = toUserProps(content?.history?.createdBy);
	const owner =
		content?.history?.ownedBy && (content?.type === 'page' || content?.type === 'blogpost')
			? toUserProps(content?.history?.ownedBy)
			: null;

	const contributorsCount = data?.contentContributors?.count || 0;
	const isOwnerContributor = data?.contentContributors?.isOwnerContributor;
	const generatedBy = content?.contentProperties?.latest?.generatedBy;

	return {
		author,
		owner,
		contributorsCount,
		isOwnerContributor,
		generatedBy,
	};
};
